import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { toast } from "sonner";
import Select from "react-select";
import { getLocalStorageData } from "../../services/axiosComponent";
import { selectStyle } from "../../services/constant";
import {
  addForms,
  updateForms,
  getFormsDetails,
} from "../../services/Users-services";
import {
  callToast,
  prepareSiteList,
  showErrorMessages,
} from "../../services/utilityFunctions.js";

const DropdownIndicator = (props) => {
  return (
    <div {...props.innerProps}>
      <div
        className="spinner-border text-primary spinner-border-sm m-2"
        role="status"
      >
        <span className="sr-only">Loading...</span>
      </div>
    </div>
  );
};

const SaveFormModal = ({
  navigate,
  refresh,
  closeModal,
  isEditing = false,
  existingFormId = null,
  navbarFormData = null, // Add this prop
}) => {
  const customFormBuilder = useSelector((state) => state.customFormBuilder);
  const profileDetails = getLocalStorageData("profileDetails");

  // Form field options
  const [regionOptions, setRegionOptions] = useState([]);
  const [siteOptions, setSiteOptions] = useState([]);
  const [primaryFieldOptions, setPrimaryFieldOptions] = useState([]);

  // Loading states
  const [loading, setLoading] = useState(false);
  const [isSiteOptionsLoading, setIsSiteOptionsLoading] = useState(false);
  const [isRegionSearchable, setIsRegionSearchable] = useState(true);
  const [isSiteSearchable, setIsSiteSearchable] = useState(true);

  // Error state
  const [errors, setErrors] = useState({});

  // Form data state - Update to use navbar data when available
  const [formData, setFormData] = useState({
    name: navbarFormData?.title || "",
    description: navbarFormData?.description || "",
    primaryField: { value: "", label: "" },
    siteId: { value: "", label: "" },
    regionId: { value: "", label: "" },
  });

  // Update form data when navbarFormData changes
  useEffect(() => {
    if (navbarFormData) {
      setFormData((prev) => ({
        ...prev,
        name: navbarFormData.title || "",
        description: navbarFormData.description || "",
      }));
    }
  }, [navbarFormData]);

  // Initialize region list
  const getRegionList = async () => {
    const data = profileDetails;

    const list = JSON.parse(localStorage.getItem("regionList")) || [];

    if (
      list.length === 1 &&
      list[0].value === data._REGION &&
      data["_REGION_ACCESS_"] === null
    ) {
      setIsRegionSearchable(false);
      handleChange("regionId", {
        value: list[0].value,
        label: list[0].label,
      });
    }

    setRegionOptions(list);
  };

  // Get sites based on selected region
  const getSiteList = async (regionId, query) => {
    setIsSiteOptionsLoading(true);
    const data = profileDetails;
    const list = await prepareSiteList(regionId, navigate, query);

    if (
      list.length === 1 &&
      list[0].value === data._SITE &&
      data["_SITE ACCESS_"] === null
    ) {
      setIsSiteSearchable(false);
      handleChange("siteId", {
        value: list[0].value,
        label: list[0].label,
      });
    }

    setIsSiteOptionsLoading(false);
    if (list.length === 0) {
      setErrors((prev) => ({
        ...prev,
        site: "No site available for this region",
      }));
    } else {
      setErrors((prev) => ({
        ...prev,
        site: "",
      }));
    }

    setSiteOptions(list);
  };

  // Extract form fields for primary field dropdown
  // Extract form fields for primary field dropdown
  const extractFormFields = () => {
    const fields = [];
    const fieldNames = new Set(); // Track field names to ensure uniqueness

    customFormBuilder.forEach((section) => {
      if (section.elements && section.elements.length > 0) {
        section.elements.forEach((element) => {
          // Only include email and number input type elements as primary fields
          const isPrimaryFieldType =
            (element.id === "text-input" &&
              element.style?.inputType === "email" &&
              element.style?.isRequired == true) ||
            (element.id === "text-input" &&
              element.style?.inputType === "mobile" &&
              element.style?.isRequired == true);

          if (isPrimaryFieldType) {
            // Get the label from element styling
            const label = element.style?.label?.label || "Unnamed Field";

            // Skip duplicate field names
            if (fieldNames.has(label)) return;
            fieldNames.add(label);

            fields.push({
              value: element.instanceId,
              label: label,
              type: element.style.inputType,
            });
          }
        });
      }
    });

    setPrimaryFieldOptions(fields);
  };

  // Handle form field changes
  const handleChange = (field, value) => {
    setErrors({});

    // Reset site when region changes
    if (field === "regionId") {
      setFormData((prev) => ({
        ...prev,
        siteId: { value: "", label: "" },
      }));
    }

    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  // Form validation
  // Form validation
  // Form validation
  const validateFields = () => {
    let isValid = true;
    let errors = {};

    if (formData.name === "") {
      isValid = false;
      errors.name = "Form Name is required";
    }

    if (formData.description === "") {
      isValid = false;
      errors.description = "Description is required";
    }

    // Check if primary field options exist
    if (primaryFieldOptions.length === 0) {
      isValid = false;
      errors.primaryField =
        "No eligible fields found. Add at least one email or number field to use as primary field";
    } else if (formData.primaryField.value === "") {
      isValid = false;
      errors.primaryField = "Primary Field is required";
    }

    if (formData.regionId.value === "") {
      isValid = false;
      errors.region = "Region is required";
    }

    if (formData.siteId.value === "") {
      isValid = false;
      errors.site = "Site is required";
    }

    // Check if there are duplicate field names in the form
    const fieldNames = new Map();
    let hasDuplicates = false;

    customFormBuilder.forEach((section, sectionIndex) => {
      if (!section.elements) return;

      section.elements.forEach((element, elementIndex) => {
        if (
          ["divider", "text", "text-title", "button", "image"].includes(
            element.id
          )
        ) {
          return; // Skip elements that don't need naming validation
        }

        const elementLabel = element.style?.label?.label || "Unnamed Field";

        if (fieldNames.has(elementLabel)) {
          hasDuplicates = true;
          const existingField = fieldNames.get(elementLabel);
          console.warn(
            `Duplicate field name "${elementLabel}" found in section ${
              sectionIndex + 1
            } and section ${existingField.section + 1}.`
          );
        } else {
          fieldNames.set(elementLabel, {
            section: sectionIndex,
            index: elementIndex,
          });
        }
      });
    });

    if (hasDuplicates) {
      isValid = false;
      errors.form =
        "Your form contains fields with duplicate names. Each field must have a unique name.";
    }

    setErrors(errors);
    return isValid;
  };

  const returnUpperCase = (str) => {
    return str.toUpperCase();
  };
  const prepareName = (str) => {
    //convert space to underScore
    return str.replace(/\s+/g, "_");
  };

  // Prepare fields array for submission
  const prepareFieldsArray = () => {
    const fields = [];
    const usedNames = new Set(); // To ensure unique field names

    customFormBuilder.forEach((section) => {
      if (section.elements && section.elements.length > 0) {
        section.elements.forEach((element) => {
          // Map form elements to field types
          let fieldType = "TEXT";

          switch (element.id) {
            case "number-input":
              fieldType = "NUMBER";
              break;
            case "textarea":
              fieldType = "TEXTAREA";
              break;
            case "checkbox":
              fieldType = "BOOLEAN";
              break;
            case "radio":
              fieldType = "BOOLEAN";
              break;
            case "select":
              fieldType = "SELECT";
              break;
            case "multiSelect":
              fieldType = "MULTISELECT";
              break;
            case "file-input":
              fieldType = "FILE";
              break;
            case "date":
              fieldType = "DATE";
              break;
            case "time":
              fieldType = "TIME";
              break;
            default:
              fieldType = "TEXT";
          }

          // Only add fields that can store data
          if (
            !["divider", "text", "text-title", "button", "image"].includes(
              element.id
            )
          ) {
            const fieldName = element.style?.label?.label || "Unnamed Field";

            // Handle duplicate field names by adding an index
            let uniqueFieldName = fieldName;
            let counter = 1;

            while (usedNames.has(uniqueFieldName)) {
              uniqueFieldName = `${fieldName}_${counter}`;
              counter++;
            }

            usedNames.add(uniqueFieldName);

            fields.push({
              title: uniqueFieldName,
              name: prepareName(uniqueFieldName.toLowerCase()),
              type: returnUpperCase(element.style.inputType) || fieldType,
              unique: element.style?.isUnique ? "Y" : "N",
              required: element.style?.isRequired ? "Y" : "N",
              regex: element.style?.customRegex || "",
              min: element.style?.minValue || element.style?.minLength || 1,
              max: element.style?.maxValue || element.style?.maxLength || 100,
            });
          }
        });
      }
    });

    return fields;
  };
  const isOpenedInNewTab = () => {
    // Check if opened via window.open (has opener) or if URL contains a flag
    return (
      (window.opener && !window.opener.closed) ||
      new URLSearchParams(window.location.search).get("newTab") === "true"
    );
  };
  useEffect(() => {
    getRegionList();
    extractFormFields();

    // Listen for refresh signals from child tabs (if this is a parent tab)
    const handleStorageChange = (e) => {
      if (e.key === "formSaved" && e.newValue === "true") {
        // Refresh the table data
        if (refresh) {
          refresh({ columnRefresh: false, isLoading: false });
        }
        // Clear the flag
        localStorage.removeItem("formSaved");
      }
    };

    window.addEventListener("storage", handleStorageChange);

    return () => {
      window.removeEventListener("storage", handleStorageChange);
    };
  }, [refresh]);
  useEffect(() => {
    if (isEditing && existingFormId) {
      const loadExistingFormData = async () => {
        try {
          const response = await getFormsDetails(existingFormId);
          if (response.status === "success") {
            const data = response.data;
            setFormData({
              name: data.NAME || "",
              description: data.DESCRIPTION || "",
              primaryField: {
                value: data["_PRIMARY FIELD"] || "",
                label: data["_PRIMARY FIELD"] || "",
              },
              siteId: {
                value: data._SITE || "",
                label: data.SITE || "",
              },
              regionId: {
                value: data._REGION || "",
                label: data.REGION || "",
              },
            });
          }
        } catch (error) {
          console.error("Error loading form data:", error);
        }
      };

      loadExistingFormData();
    }
  }, [isEditing, existingFormId]);

  // Handle form submission
  // In saveForm.js, modify the handleSubmit function's success block

  const handleSubmit = async () => {
    if (validateFields()) {
      setLoading(true);

      try {
        // Prepare metadata as string
        const metadata = JSON.stringify(customFormBuilder);

        // Create fields array
        const fields = prepareFieldsArray();

        // Create submission data
        const submitData = {
          name: formData.name,
          description: formData.description,
          primaryField: prepareName(formData.primaryField.label.toLowerCase()),
          siteId: parseInt(formData.siteId.value),
          regionId: parseInt(formData.regionId.value),
          organizationId: profileDetails["_ORGANIZATION_"]._ID,
          metadata: metadata,
          fields: fields,
        };
        if (isEditing && existingFormId) {
          submitData.id = existingFormId;
        }

        console.log("Form submission data:", submitData);

        const res = isEditing
          ? await updateForms(submitData) // You'll need to implement this API function
          : await addForms(submitData);

        if (res.status === "success") {
          callToast("success", "Created", "Form saved successfully");

          // Check if this is a new tab that should be closed
          if (isOpenedInNewTab()) {
            try {
              // Signal parent tab to refresh using localStorage
              localStorage.setItem("formSaved", "true");

              // Try to refresh parent tab directly if possible
              if (window.opener && !window.opener.closed) {
                try {
                  // If same origin, we can directly call refresh
                  window.opener.postMessage(
                    { type: "FORM_SAVED" },
                    window.location.origin
                  );
                } catch (crossOriginError) {
                  console.warn(
                    "Cross-origin restriction, using localStorage method"
                  );
                }
              }

              // Small delay to ensure message is sent, then close tab
              setTimeout(() => {
                window.close();
              }, 300);
            } catch (error) {
              console.error("Error communicating with parent tab:", error);
              // Fallback: just close the tab
              window.close();
            }
          } else {
            // This is not a new tab, handle normally (modal mode)
            if (refresh) {
              await refresh({ columnRefresh: false, isLoading: false });
            }
            setLoading(false);
            closeModal();
          }
        } else {
          setLoading(false);
          showErrorMessages({
            data: res,
            navigate,
          });
        }
      } catch (error) {
        console.error("Error saving form:", error);
        setLoading(false);
        callToast("error", "Error", "Failed to save form");
      }
    }
  };
  // Initialize data
  useEffect(() => {
    getRegionList();
    extractFormFields();
  }, []);

  return (
    <section className="login-content">
      <div className="">
        <div className="d-flex">
          <div className="w-100 ps-5 pe-5">
            <form>
              <div className="mb-3">
                <div className="form-row">
                  <div className="col">
                    <label className="form-label">Form Name</label>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Enter Form Name"
                      value={formData.name}
                      onChange={(e) => handleChange("name", e.target.value)}
                    />
                    {errors.name && (
                      <small className="text-danger">{errors.name}</small>
                    )}
                  </div>
                  <div className="col mt-3">
                    <label className="form-label">Description</label>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Enter Form Description"
                      value={formData.description}
                      onChange={(e) =>
                        handleChange("description", e.target.value)
                      }
                    />
                    {errors.description && (
                      <small className="text-danger">
                        {errors.description}
                      </small>
                    )}
                  </div>
                </div>
              </div>

              <div className="mb-3">
                <label className="form-label">Primary Field</label>
                <Select
                  styles={selectStyle}
                  options={primaryFieldOptions}
                  value={
                    formData.primaryField.label === ""
                      ? null
                      : formData.primaryField
                  }
                  onChange={(newValue) => {
                    handleChange(
                      "primaryField",
                      newValue == null ? { value: "", label: "" } : newValue
                    );
                  }}
                  isSearchable={true}
                  placeholder="Select Primary Field"
                  className="basic-single"
                  classNamePrefix="select"
                  filterOption={(option, inputValue) =>
                    option.label
                      .toLowerCase()
                      .includes(inputValue.toLowerCase())
                  }
                />
                {errors.primaryField && (
                  <small className="text-danger">{errors.primaryField}</small>
                )}
                <small className="text-muted">
                  Select the main field that will be used to identify this
                  form's records
                </small>
              </div>

              <div className="mb-3">
                <label className="form-label">Region</label>
                <Select
                  isDisabled={isEditing} // This will disable when isEditing is true
                  options={regionOptions}
                  value={
                    formData.regionId.label === "" ? null : formData.regionId
                  }
                  onChange={(newValue) => {
                    handleChange(
                      "regionId",
                      newValue == null ? { value: "", label: "" } : newValue
                    );

                    if (newValue != null) {
                      getSiteList(newValue.value);
                    }
                  }}
                  noOptionsMessage={() => {
                    return !isRegionSearchable
                      ? "Region Access not available"
                      : "No matches found";
                  }}
                  isSearchable={isRegionSearchable}
                  placeholder="Select Region"
                  className="basic-single"
                  classNamePrefix="select"
                  filterOption={(option, inputValue) =>
                    option.label
                      .toLowerCase()
                      .includes(inputValue.toLowerCase())
                  }
                  styles={selectStyle}
                />
                {errors.region && (
                  <small className="text-danger">{errors.region}</small>
                )}
              </div>

              <div className="mb-3">
                <label className="form-label">Site</label>
                <Select
                  isDisabled={isEditing == true}
                  styles={selectStyle}
                  options={siteOptions}
                  value={formData.siteId.label === "" ? null : formData.siteId}
                  components={
                    isSiteOptionsLoading ? { DropdownIndicator } : null
                  }
                  onChange={(newValue) => {
                    handleChange(
                      "siteId",
                      newValue == null ? { value: "", label: "" } : newValue
                    );
                  }}
                  onInputChange={(inputValue, { action }) => {
                    if (action === "input-change") {
                      getSiteList(formData.regionId.value, inputValue);
                    }
                  }}
                  noOptionsMessage={() => {
                    return !isSiteSearchable
                      ? "Site Access Not available"
                      : "No matches found";
                  }}
                  isSearchable={isSiteSearchable}
                  placeholder="Select Site"
                  className="basic-single"
                  classNamePrefix="select"
                  filterOption={(option, inputValue) =>
                    option.label
                      .toLowerCase()
                      .includes(inputValue.toLowerCase())
                  }
                />
                {errors.site && (
                  <small className="text-danger">{errors.site}</small>
                )}
              </div>

              {errors.form && (
                <div className="alert alert-danger" role="alert">
                  {errors.form}
                </div>
              )}

              <div className="d-flex justify-content-end mt-4 gap-2">
                <button
                  type="button"
                  className="btn btn-secondary me-2"
                  onClick={closeModal}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleSubmit}
                  disabled={loading}
                >
                  {loading ? (
                    <div className="spinner-border text-light" role="status">
                      <span className="sr-only">Loading...</span>
                    </div>
                  ) : isEditing ? (
                    "Update Form"
                  ) : (
                    "Save Form"
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

export default SaveFormModal;
